import React from 'react';
import { Phone, Mail, Clock, Check, ArrowRight, Diamond } from 'lucide-react';
import { companyInfo } from '../../data/mock';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const Contact = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  
  return (
    <section id="contact" ref={sectionRef} className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1512] to-[#0D0B09]" />
      
      {/* Decorative */}
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-[#C9A97B]/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-[#472F27]/20 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-blurIn' : 'opacity-0'}`}>
          <span className={`inline-block px-4 py-1.5 rounded-full bg-[#C9A97B]/10 text-[#C9A97B] text-sm font-medium mb-4 ${isVisible ? 'animate-zoomIn' : 'opacity-0'}`} style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
            İletişim
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#F5F0E8] mb-4">
            Bizimle <span className="text-gold-gradient">İletişime Geçin</span>
          </h2>
          
          {/* Decorative line */}
          <div className={`flex items-center justify-center gap-3 mb-4 ${isVisible ? 'animate-expandWidth' : 'opacity-0'}`} style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#C9A97B]/40" />
            <Diamond className="w-3 h-3 text-[#C9A97B]/40" />
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#C9A97B]/40" />
          </div>
          
          <p className="text-[#BFB5A5] text-lg max-w-2xl mx-auto">
            Sorularınız için bize ulaşın. Uzman ekibimiz size yardımcı olmaktan mutluluk duyacaktır.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div className={`bg-gradient-to-br from-[#1F1914] to-[#1A1512] rounded-2xl p-6 md:p-8 border border-[#C9A97B]/10 h-full ${isVisible ? 'animate-slideInLeft' : 'opacity-0'}`} style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
            <h3 className="text-2xl font-display font-bold text-[#F5F0E8] mb-8">
              İletişim Bilgileri
            </h3>

            <div className="space-y-6">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-4 group p-3 rounded-xl hover:bg-[#C9A97B]/5 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#C9A97B]/10 flex items-center justify-center group-hover:bg-[#C9A97B]/20 transition-colors">
                  <Phone className="w-5 h-5 text-[#C9A97B]" />
                </div>
                <div>
                  <div className="text-[#7A7067] text-sm">Telefon</div>
                  <div className="text-[#F5F0E8] font-medium group-hover:text-[#C9A97B] transition-colors">
                    {companyInfo.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-4 group p-3 rounded-xl hover:bg-[#C9A97B]/5 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#C9A97B]/10 flex items-center justify-center group-hover:bg-[#C9A97B]/20 transition-colors">
                  <Mail className="w-5 h-5 text-[#C9A97B]" />
                </div>
                <div>
                  <div className="text-[#7A7067] text-sm">E-posta</div>
                  <div className="text-[#F5F0E8] font-medium group-hover:text-[#C9A97B] transition-colors">
                    {companyInfo.email}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-3">
                <div className="w-12 h-12 rounded-xl bg-[#C9A97B]/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#C9A97B]" />
                </div>
                <div>
                  <div className="text-[#7A7067] text-sm">Çalışma Saatleri</div>
                  <div className="text-[#F5F0E8] font-medium">Pazartesi - Cumartesi: 09:00 - 18:00</div>
                </div>
              </div>
            </div>
          </div>

          {/* Neden Tam Ayar? */}
          <div className={`bg-gradient-to-br from-[#1F1914] to-[#1A1512] rounded-2xl p-6 md:p-8 border border-[#C9A97B]/10 relative overflow-hidden h-full ${isVisible ? 'animate-slideInRight' : 'opacity-0'}`} style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
            {/* Decorative corner */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A97B]/5 rounded-bl-full" />
            
            <div className="relative">
              <span className="inline-block px-3 py-1 rounded-full bg-[#C9A97B]/10 text-[#C9A97B] text-xs font-medium mb-4">
                Avantajlarımız
              </span>
              <h3 className="text-2xl font-display font-bold text-[#F5F0E8] mb-4">
                Neden <span className="text-gold-gradient">Tam Ayar</span>?
              </h3>
              <p className="text-[#BFB5A5] text-sm leading-relaxed mb-6">
                20+ yıllık sektör deneyimimiz ve kuyumcuların gerçek ihtiyaçlarını bilerek 
                geliştirdiğimiz çözümlerle yanınızdayız.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  { title: '7/24 Teknik Destek', desc: 'Her an yanınızdayız' },
                  { title: 'Ücretsiz Kurulum ve Eğitim', desc: 'Hemen başlayın' },
                  { title: '30 Gün Para İade Garantisi', desc: 'Risk almayın' },
                  { title: 'Sınırsız Güncelleme', desc: 'Her zaman güncel kalın' },
                  { title: 'Veri Güvenliği', desc: '%100 bulut yedekleme' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 group">
                    <div className="w-6 h-6 rounded-full bg-[#C9A97B]/20 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#C9A97B]/30 transition-colors">
                      <Check className="w-3.5 h-3.5 text-[#C9A97B]" />
                    </div>
                    <div>
                      <span className="text-[#F5F0E8] font-medium">{item.title}</span>
                      <span className="text-[#7A7067] text-sm ml-2">— {item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href={`mailto:satis@tamayar.com`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#C9A97B]/10 text-[#C9A97B] font-medium hover:bg-[#C9A97B]/20 transition-colors group"
              >
                <span>Hemen İletişime Geç</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
